import { createColumnHelper } from '@tanstack/react-table';

import TableColumnHeader from '../TableColumnHeader';

import type { ReactNode } from 'react';
import type { BaseColumnOptions, ColumnFactory } from './types';

/**
 * Options for custom columns
 */
export interface CustomColumnOptions extends BaseColumnOptions {
  /**
   * Enable sorting for this column
   */
  sortable?: boolean;
  /**
   * Tooltip to display next to the column header
   */
  infoTooltip?: string;
}

/**
 * Cell context passed to the render function
 */
export interface CustomCellContext<TData, TValue = unknown> {
  /**
   * Get the cell value
   */
  getValue: () => TValue;
  /**
   * The original row data
   */
  row: TData;
}

/**
 * Factory function to create a column with custom cell rendering
 */
export function createCustomColumn<TData, TValue = unknown>(config: {
  accessor: keyof TData & string;
  label: string;
  id?: string;
  options?: CustomColumnOptions;
  cell: (context: CustomCellContext<TData, TValue>) => ReactNode;
}): ColumnFactory<TData, TValue>;

export function createCustomColumn<TData, TValue = unknown>(config: {
  accessorFn: (row: TData) => TValue;
  id: string;
  label: string;
  options?: CustomColumnOptions;
  cell: (context: CustomCellContext<TData, TValue>) => ReactNode;
}): ColumnFactory<TData, TValue>;

export function createCustomColumn<TData, TValue = unknown>(config: {
  accessor?: keyof TData & string;
  accessorFn?: (row: TData) => TValue;
  id?: string;
  label: string;
  options?: CustomColumnOptions;
  cell: (context: CustomCellContext<TData, TValue>) => ReactNode;
}): ColumnFactory<TData, TValue> {
  const { accessor, accessorFn, id, label, options = {}, cell } = config;
  const { size = 150, sortable = false, className, infoTooltip, meta } = options;

  const columnHelper = createColumnHelper<TData>();

  const columnId = id || (accessor as string);

  if (accessorFn) {
    // Use accessor function
    return columnHelper.accessor(accessorFn, {
      id: columnId,
      size,
      header: ({ table }) =>
        sortable ? (
          <TableColumnHeader
            accessor={columnId}
            infoTooltip={infoTooltip}
            label={label}
            setSorting={table.setSorting}
            sortable
            tableState={table.getState()}
          />
        ) : (
          <TableColumnHeader
            accessor={columnId}
            infoTooltip={infoTooltip}
            label={label}
            tableState={table.getState()}
          />
        ),
      cell: (info) =>
        cell({
          getValue: () => info.getValue() as TValue,
          row: info.row.original,
        }),
      meta,
    }) as ColumnFactory<TData, TValue>;
  }

  // Use simple accessor
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return columnHelper.accessor(accessor as any, {
    id: columnId,
    size,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    header: ({ table }: { table: any }) =>
      sortable ? (
        <TableColumnHeader
          className={className}
          accessor={columnId}
          infoTooltip={infoTooltip}
          label={label}
          setSorting={table.setSorting}
          sortable
          tableState={table.getState()}
        />
      ) : (
        <TableColumnHeader
          className={className}
          accessor={columnId}
          infoTooltip={infoTooltip}
          label={label}
          tableState={table.getState()}
        />
      ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) =>
      cell({
        getValue: () => info.getValue() as TValue,
        row: info.row.original as TData,
      }),
    meta,
  }) as ColumnFactory<TData, TValue>;
}
